import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setImpersonationHeader, setAccessToken, getAccessToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [permissions, setPermissions] = useState({ wildcard: false, perms: [] });

  const load = useCallback(async () => {
    if (!getAccessToken()) {
      // No token in memory — still try (cookie may be present), but if 401 we go anonymous
    }
    try {
      const me = await api.get("/auth/me");
      setUser(me.data.user);
      try {
        const p = await api.get("/auth/permissions");
        setPermissions(p.data);
      } catch (_) {}
    } catch (_) {
      setUser(false);
      setAccessToken(null);
      setPermissions({ wildcard: false, perms: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = async (email, password) => {
    // R1d.13 — trim client-side to catch copy-paste whitespace before the
    // wire. Matches the strip already applied to email + password on the
    // backend as a defence-in-depth layer.
    const cleanEmail = (email || "").trim();
    const cleanPassword = (password || "").trim();
    const r = await api.post("/auth/login", { email: cleanEmail, password: cleanPassword });
    // MFA: an enrolled user gets a second step; no session exists until /auth/mfa/complete succeeds.
    if (r.data.mfa_required) return { mfa: r.data };
    return finishLogin(r);
  };

  const completeMfa = async (body) => finishLogin(await api.post("/auth/mfa/complete", body));

  const finishLogin = async (r) => {
    if (r.data.access_token) setAccessToken(r.data.access_token);
    setUser(r.data.user);
    try {
      const p = await api.get("/auth/permissions");
      setPermissions(p.data);
    } catch (_) {}
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
    setImpersonation(null);
    setImpersonationHeader(null);
    setAccessToken(null);
    setPermissions({ wildcard: false, perms: [] });
  };

  const startImpersonation = async (target) => {
    const r = await api.post("/portal/impersonate/start", { target });
    // R1c dead-click fix F1+F2 (Round 2) — set header + context BEFORE the
    // permissions refetch so the global banner mounts on the next React tick.
    setImpersonationHeader(target);
    setImpersonation({ target, label: r.data.label });
    try {
      const p = await api.get("/auth/permissions");
      setPermissions(p.data);
    } catch (_) {}
    return r.data;
  };

  const stopImpersonation = async () => {
    // R1c dead-click fix F1+F2 (Round 2) — Optimistic unmount. Clear local
    // impersonation state + header FIRST so the banner disappears within
    // one React tick. The server-side POST fires in the background; audit
    // ledger still records the stop event.
    setImpersonation(null);
    setImpersonationHeader(null);
    try { await api.post("/portal/impersonate/stop"); } catch (_) {}
    try {
      const p = await api.get("/auth/permissions");
      setPermissions(p.data);
    } catch (_) {}
  };

  const can = (resource, action) => {
    if (!permissions) return false;
    if (permissions.wildcard) return true;
    return (permissions.perms || []).some(
      ([r, a]) => (r === "*" || r === resource) && (a === "*" || a === action)
    );
  };

  return (
    <AuthCtx.Provider
      value={{ user, permissions, impersonation, login, completeMfa, logout, load,
               startImpersonation, stopImpersonation, can, setUser }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
